/**
 * ResidentPickupSection — S1 addendum. Trash & Recycling pickups: manual
 * request + QR can scan, both gated to operational days.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trash2, Recycle, QrCode, CalendarX, X } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';
const inputCls = 'w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-white text-sm focus:border-[#C9A962] focus:outline-none';
const labelCls = 'text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block';

export default function ResidentPickupSection({ unitNumber }) {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(null); // null | 'manual' | 'scan'
  const [f, setF] = useState({ kind: 'trash', location: '', preferred_time: '' });
  const [scanned, setScanned] = useState(null);
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);

  const load = useCallback(async () => {
    try { setStatus(await apiFetch('/api/resident/pickup/status')); } catch (_) { /* held */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); scannerRef.current.clear(); } catch (_) { /* already stopped */ }
      scannerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (mode === 'scan' && !scanned) {
      const t = setTimeout(async () => {
        try {
          const sc = new Html5Qrcode('resident-qr-reader');
          scannerRef.current = sc;
          await sc.start({ facingMode: 'environment' }, { fps: 10, qrbox: 200 },
            (text) => { setScanned(text); stopScanner(); }, () => {});
        } catch (e) { toast.error('Camera unavailable — you can use Request Pickup instead'); setMode('manual'); }
      }, 100);
      return () => { clearTimeout(t); stopScanner(); };
    }
    return undefined;
  }, [mode, scanned, stopScanner]);

  const submitManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiFetch('/api/resident/pickup/request', { method: 'POST', body: f });
      toast.success(`${f.kind === 'trash' ? 'Trash' : 'Recycling'} pickup requested${res.notified_agent ? ` — ${res.notified_agent} notified` : ' — team notified'}`);
      setMode(null); setF({ kind: 'trash', location: '', preferred_time: '' });
    } catch (err) { toast.error(err.detail || 'Request failed'); }
    finally { setBusy(false); }
  };

  const placeAction = async (action) => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/resident/pickup/qr-scan', { method: 'POST', body: { can_code: scanned, action } });
      toast.success(`Place ${action} logged${res.notified_agent ? ` — ${res.notified_agent} notified` : ' — team notified'}`);
      setMode(null); setScanned(null);
    } catch (err) { toast.error(err.detail || 'Scan failed'); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const offDay = !status.operational_today;

  return (
    <section data-testid="resident-pickup-section">
      <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Trash &amp; Recycling</h2>

      {offDay ? (
        <div className="rounded-xl border border-amber-800 bg-amber-950/30 p-4 flex items-start gap-3" data-testid="pickup-offday-message">
          <CalendarX className="h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-bold text-white">No pickup service today</p>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Next available pickup day: <span className="font-bold text-amber-400">{status.next_available_day}</span> ({status.next_available_date})
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => { setMode(mode === 'manual' ? null : 'manual'); setScanned(null); }}
              className="flex flex-col items-center gap-1.5 rounded-xl border p-4 transition-colors"
              style={mode === 'manual' ? { borderColor: GOLD, backgroundColor: 'rgba(201,169,98,0.08)' } : { borderColor: '#3f3f46' }}
              data-testid="pickup-request-btn">
              <Trash2 className="h-5 w-5" style={{ color: GOLD }} />
              <span className="text-xs font-black uppercase tracking-wider text-white">Request Pickup</span>
            </button>
            <button onClick={() => { setMode(mode === 'scan' ? null : 'scan'); setScanned(null); }}
              className="flex flex-col items-center gap-1.5 rounded-xl border p-4 transition-colors"
              style={mode === 'scan' ? { borderColor: GOLD, backgroundColor: 'rgba(201,169,98,0.08)' } : { borderColor: '#3f3f46' }}
              data-testid="pickup-scan-btn">
              <QrCode className="h-5 w-5" style={{ color: GOLD }} />
              <span className="text-xs font-black uppercase tracking-wider text-white">Scan Can QR</span>
            </button>
          </div>

          {mode === 'manual' && (
            <form onSubmit={submitManual} className="mt-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-3" data-testid="pickup-manual-form">
              <div>
                <label className={labelCls}>Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[['trash', 'Trash', Trash2], ['recycling', 'Recycling', Recycle]].map(([k, l, Icon]) => (
                    <button type="button" key={k} onClick={() => setF((s) => ({ ...s, kind: k }))}
                      className="flex items-center justify-center gap-1.5 rounded-lg border py-2.5 text-xs font-bold uppercase"
                      style={f.kind === k ? { backgroundColor: GOLD, color: '#1a1a1a', borderColor: GOLD } : { color: '#a1a1aa', borderColor: '#3f3f46' }}
                      data-testid={`pickup-kind-${k}`}>
                      <Icon className="h-3.5 w-3.5" /> {l}
                    </button>
                  ))}
                </div>
              </div>
              <div><label className={labelCls}>Unit</label>
                <input readOnly value={unitNumber || ''} className={`${inputCls} opacity-60`} data-testid="pickup-unit" /></div>
              <div><label className={labelCls}>Building / Location</label>
                <input placeholder="e.g. Building 2, breezeway" value={f.location}
                  onChange={(e) => setF((s) => ({ ...s, location: e.target.value }))} className={inputCls} data-testid="pickup-location" /></div>
              <div><label className={labelCls}>Preferred Pickup Time</label>
                <input type="time" value={f.preferred_time}
                  onChange={(e) => setF((s) => ({ ...s, preferred_time: e.target.value }))} className={inputCls} data-testid="pickup-time" /></div>
              <button type="submit" disabled={busy}
                className="w-full py-3 rounded-full font-black uppercase tracking-widest text-xs disabled:opacity-50"
                style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="pickup-submit-btn">
                {busy ? 'Sending…' : 'Send Request'}
              </button>
            </form>
          )}

          {mode === 'scan' && (
            <div className="mt-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4" data-testid="pickup-scan-panel">
              {!scanned ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] text-zinc-400">Point your camera at the QR code on your can</p>
                    <button onClick={() => { stopScanner(); setMode(null); }} data-testid="pickup-scan-close"><X className="h-4 w-4 text-zinc-500" /></button>
                  </div>
                  <div id="resident-qr-reader" className="rounded-lg overflow-hidden" />
                </>
              ) : (
                <div className="text-center" data-testid="pickup-scan-prompt">
                  <p className="text-sm font-bold text-white mb-3">Can scanned — what are you placing?</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => placeAction('trash')} disabled={busy}
                      className="py-3 rounded-full font-black uppercase tracking-widest text-xs disabled:opacity-50"
                      style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="pickup-place-trash">
                      Place Trash
                    </button>
                    <button onClick={() => placeAction('recycling')} disabled={busy}
                      className="py-3 rounded-full font-black uppercase tracking-widest text-xs border disabled:opacity-50"
                      style={{ borderColor: GOLD, color: GOLD }} data-testid="pickup-place-recycling">
                      Place Recycling
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
